/**
 * Lesson Player Page - Video playback with notes and progress tracking
 */
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Navigation } from '../../components/layout/Navigation';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import { useCurrentUser } from '../../contexts/AuthContext';
import { api } from '../../services/api/endpoints';
import { paymentsApi } from '../../services/api/payments';
import { hasCourseAccess, markCoursePurchased } from '../../services/billing/local-billing';
import type { LessonNote } from '../../types/models';
import { formatDuration } from '../../utils/fp-utils';

const getVideoMimeType = (videoUrl: string): string | undefined => {
  const normalized = videoUrl.toLowerCase();
  if (normalized.endsWith('.mp4')) return 'video/mp4';
  if (normalized.endsWith('.mov')) return 'video/quicktime';
  if (normalized.endsWith('.webm')) return 'video/webm';
  if (normalized.endsWith('.ogg') || normalized.endsWith('.ogv')) return 'video/ogg';
  if (normalized.endsWith('.m3u8')) return 'application/x-mpegURL';
  return undefined;
};

/**
 * Video player component
 */
const VideoPlayer: React.FC<{
  videoUrl: string;
  onTimeUpdate: (currentTime: number) => void;
  onEnded: () => void;
}> = ({ videoUrl, onTimeUpdate, onEnded }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handleTimeUpdate = () => {
      onTimeUpdate(video.currentTime);
    };
    
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', onEnded);
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, [onTimeUpdate, onEnded]);
  
  const changePlaybackRate = (rate: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };
  
  return (
    <div className="relative bg-black rounded-xl overflow-hidden">
      <video
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        className="w-full aspect-video"
      >
        <source src={videoUrl} type={getVideoMimeType(videoUrl)} />
      </video>
      
      {/* Playback speed controls */}
      <div className="absolute bottom-16 right-4 flex gap-2">
        {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
          <button
            key={rate}
            onClick={() => changePlaybackRate(rate)}
            className={`px-3 py-1 rounded text-sm font-semibold transition-colors ${
              playbackRate === rate
                ? 'bg-brand-gold text-gray-900'
                : 'bg-black/70 text-white hover:bg-black/90'
            }`}
          >
            {rate}x
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Notes panel component
 */
const NotesPanel: React.FC<{
  lessonId: string;
  currentTime: number;
}> = ({ lessonId, currentTime }) => {
  const [noteContent, setNoteContent] = useState('');
  const queryClient = useQueryClient();
  
  // Fetch notes
  const { data: notes } = useQuery({
    queryKey: ['notes', lessonId],
    queryFn: async () => {
      const result = await api.lessons.getNotes(lessonId);
      if (result.success) return result.data;
      throw new Error(result.error.message);
    },
  });
  
  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: async () => {
      const result = await api.lessons.createNote(lessonId, {
        lessonId,
        content: noteContent,
        timestamp: Math.floor(currentTime),
      });
      if (!result.success) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', lessonId] });
      setNoteContent('');
      toast.success('Note saved!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save note: ${error.message}`);
    },
  });
  
  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const result = await api.lessons.deleteNote(noteId);
      if (!result.success) throw new Error(result.error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', lessonId] });
      toast.success('Note deleted');
    },
  });
  
  return (
    <div className="bg-brand-gray-800 rounded-xl p-6">
      <h3 className="text-xl font-semibold text-white mb-6">Notes</h3>
      
      {/* Create note */}
      <div className="mb-6">
        <div className="mb-2 text-sm text-gray-400">
          Timestamp: {formatDuration(Math.floor(currentTime))}
        </div>
        <textarea
          value={noteContent}
          onChange={(e) => setNoteContent(e.target.value)}
          placeholder="Add a note at this timestamp..."
          className="w-full h-32 px-4 py-3 bg-brand-gray-700 border border-brand-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-gold resize-none"
        />
        <Button
          className="mt-3"
          size="sm"
          variant="gold"
          onClick={() => createNoteMutation.mutate()}
          disabled={!noteContent.trim() || createNoteMutation.isPending}
        >
          {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
        </Button>
      </div>
      
      {/* Notes list */}
      <div className="space-y-4">
        {notes?.map((note: LessonNote) => (
          <div
            key={note.id}
            className="bg-brand-gray-700 rounded-lg p-4"
          >
            <div className="flex items-start justify-between mb-2">
              <span className="text-brand-gold text-sm font-semibold">
                {formatDuration(note.timestamp)}
              </span>
              <button
                onClick={() => deleteNoteMutation.mutate(note.id)}
                className="text-gray-400 hover:text-red-400 transition-colors"
              >
                ✕
              </button>
            </div>
            <p className="text-gray-300 text-sm">{note.content}</p>
          </div>
        ))}
        
        {notes?.length === 0 && (
          <p className="text-center text-gray-500 py-8">
            No notes yet. Add your first note!
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Lesson player page
 */
const LessonPlayerPageContent: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [currentTime, setCurrentTime] = useState(0);
  const [accessCode, setAccessCode] = useState('');
  const [redeemPending, setRedeemPending] = useState(false);
  const [accessRefreshToken, setAccessRefreshToken] = useState(0);
  
  // Fetch lesson data
  const { data: lesson, isLoading } = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: async () => {
      const result = await api.lessons.getById(lessonId!);
      if (result.success) return result.data;
      throw new Error(result.error.message);
    },
    enabled: !!lessonId,
  });

  const hasPurchasedCourseAccess = React.useMemo(
    () => (lesson ? hasCourseAccess(lesson.courseId) : false),
    [lesson?.courseId, accessRefreshToken]
  );
  const canUsePaidLesson = lesson ? (lesson.isFree || hasPurchasedCourseAccess) : false;
  const canDownloadLesson = lesson ? hasPurchasedCourseAccess : false;
  
  // Fetch course lessons for navigation
  const { data: courseLessons } = useQuery({
    queryKey: ['lessons', lesson?.courseId],
    queryFn: async () => {
      if (!lesson) return [];
      const result = await api.lessons.getByCourse(lesson.courseId);
      if (result.success) return result.data;
      return [];
    },
    enabled: !!lesson,
  });
  
  // Update progress mutation
  const updateProgressMutation = useMutation({
    mutationFn: async (watchedSeconds: number) => {
      if (!lesson) return;
      const result = await api.lessons.updateProgress(lessonId!, {
        lessonId: lessonId!,
        userId: '', // Will be set by backend
        completed: false,
        watchedSeconds,
        lastWatchedAt: new Date().toISOString(),
      });
      if (!result.success) throw new Error(result.error.message);
    },
  });
  
  // Handle time update (debounced in practice)
  const handleTimeUpdate = (time: number) => {
    if (!canUsePaidLesson) return;
    setCurrentTime(time);
    
    // Update progress every 10 seconds
    if (Math.floor(time) % 10 === 0) {
      updateProgressMutation.mutate(time);
    }
  };
  
  // Handle video ended
  const handleVideoEnded = () => {
    if (!lesson || !canUsePaidLesson) return;
    
    // Mark as completed
    updateProgressMutation.mutate(lesson.duration);
    
    // Navigate to next lesson
    const currentIndex = courseLessons?.findIndex((l) => l.id === lessonId) ?? -1;
    if (currentIndex >= 0 && courseLessons && currentIndex < courseLessons.length - 1) {
      const nextLesson = courseLessons[currentIndex + 1];
      toast.success('Lesson completed! Moving to next lesson...');
      setTimeout(() => navigate(`/lessons/${nextLesson.id}`), 2000);
    } else {
      toast.success('Congratulations! You completed this lesson!');
    }
  };
  
  // Navigate to lesson
  const goToLesson = (targetLessonId: string) => {
    navigate(`/lessons/${targetLessonId}`);
  };

  const handleRedeemCode = async () => {
    if (!lesson) return;
    const trimmed = accessCode.trim();
    if (!trimmed) {
      toast.error('Enter a code first');
      return;
    }

    setRedeemPending(true);
    const result = await paymentsApi.redeemAccessCode(trimmed, {
      courseId: lesson.courseId,
      userId: currentUser?.id,
      email: currentUser?.email,
    });
    setRedeemPending(false);

    if (!result.success || !result.data.success || !result.data.access_granted) {
      toast.error(result.success ? (result.data.reason || 'Failed to redeem code') : result.error.message);
      return;
    }

    markCoursePurchased(lesson.courseId);
    setAccessCode('');
    setAccessRefreshToken((value) => value + 1);
    toast.success('Code redeemed. Downloads are now unlocked.');
  };

  const triggerBrowserDownload = (href: string, filename: string) => {
    if (href.startsWith('blob:')) {
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = filename;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const inferVideoExtension = (videoUrl: string): string => {
    try {
      const parsed = new URL(videoUrl, window.location.origin);
      const extension = parsed.pathname.split('.').pop()?.toLowerCase();
      if (extension && /^[a-z0-9]{2,5}$/.test(extension)) {
        return extension;
      }
    } catch {
      // Keep fallback extension.
    }
    return 'mp4';
  };

  const handleDownloadVideo = async () => {
    if (!lesson) return;
    if (!canDownloadLesson) {
      toast.error('Redeem your course code to download this lesson video.');
      return;
    }

    const filenameSlug = lesson.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'lesson-video';
    const extension = inferVideoExtension(lesson.videoUrl);
    const filename = `${filenameSlug}.${extension}`;

    try {
      const parsed = new URL(lesson.videoUrl, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        triggerBrowserDownload(parsed.toString(), filename);
        toast.success('Download started.');
        return;
      }
    } catch {
      // Continue with fetch/direct fallback below.
    }

    try {
      const response = await fetch(lesson.videoUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Failed to fetch video (${response.status})`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerBrowserDownload(blobUrl, filename);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
      toast.success('Video download started.');
    } catch {
      triggerBrowserDownload(lesson.videoUrl, filename);
      toast.success('Download started.');
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }
  
  if (!lesson) {
    return (
      <div className="min-h-screen bg-brand-dark">
        <Navigation />
        <Container className="pt-28">
          <div className="text-center py-20">
            <p className="text-red-400 text-xl">Lesson not found</p>
            <Button className="mt-6" onClick={() => navigate('/courses')}>
              Browse Courses
            </Button>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-brand-dark">
      <Navigation />
      
      <main className="pt-24 pb-12">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              {canUsePaidLesson ? (
                <VideoPlayer
                  videoUrl={lesson.videoUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleVideoEnded}
                />
              ) : (
                <div className="bg-brand-gray-800 rounded-xl p-8 border border-brand-gray-700">
                  <h2 className="text-2xl font-bold text-white mb-3">Lesson Locked</h2>
                  <p className="text-gray-300 mb-6">
                    Redeem your access code to unlock this lesson and enable downloads.
                  </p>
                  <div className="flex flex-col md:flex-row gap-3">
                    <input
                      value={accessCode}
                      onChange={(event) => setAccessCode(event.target.value.toUpperCase())}
                      placeholder="ENTER-CODE-HERE"
                      className="w-full md:flex-1 h-11 px-3 bg-brand-gray-700 border border-brand-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                    />
                    <Button
                      variant="gold"
                      onClick={handleRedeemCode}
                      disabled={redeemPending || !accessCode.trim()}
                    >
                      {redeemPending ? 'Redeeming...' : 'Redeem Code'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/courses/${encodeURIComponent(lesson.courseId)}`)}
                    >
                      Back to Course
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Lesson info */}
              <div className="mt-6">
                <h1 className="text-3xl font-bold text-white mb-4">
                  {lesson.title}
                </h1>
                <p className="text-gray-300 leading-relaxed">
                  {lesson.description}
                </p>

                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadVideo}
                    disabled={!canDownloadLesson}
                  >
                    {canDownloadLesson ? 'Download Lesson Video' : 'Redeem Code to Download'}
                  </Button>
                </div>
                
                {/* Resources */}
                {lesson.resources.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Resources
                    </h3>
                    <div className="space-y-2">
                      {lesson.resources.map((resource) => (
                        <a
                          key={resource.id}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-4 bg-brand-gray-800 rounded-lg hover:bg-brand-gray-700 transition-colors"
                        >
                          <span className="text-2xl">
                            {resource.type === 'pdf' ? '📄' : '🔗'}
                          </span>
                          <span className="text-white">{resource.title}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Notes panel */}
              {canUsePaidLesson && (
                <div className="mt-8">
                  <NotesPanel lessonId={lessonId!} currentTime={currentTime} />
                </div>
              )}
            </div>
            
            {/* Sidebar - Course navigation */}
            <div className="lg:col-span-1">
              <div className="bg-brand-gray-800 rounded-xl p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Course Content
                </h3>
                
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {courseLessons?.map((courseLesson, index) => (
                    <button
                      key={courseLesson.id}
                      onClick={() => goToLesson(courseLesson.id)}
                      disabled={!courseLesson.isFree && !hasPurchasedCourseAccess}
                      className={`w-full text-left p-4 rounded-lg transition-colors ${
                        courseLesson.id === lessonId
                          ? 'bg-brand-gold text-gray-900'
                          : !courseLesson.isFree && !hasPurchasedCourseAccess
                            ? 'bg-brand-gray-700/40 text-gray-500 cursor-not-allowed'
                            : 'bg-brand-gray-700 text-white hover:bg-brand-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-sm">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {courseLesson.title}
                          </div>
                          <div className="text-sm opacity-75">
                            {formatDuration(courseLesson.duration)}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </main>
    </div>
  );
};

/**
 * Wrap lesson player in protected route
 */
export const LessonPlayerPage: React.FC = () => (
  <ProtectedRoute>
    <LessonPlayerPageContent />
  </ProtectedRoute>
);
